import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/layouts/app-layout';
import { LoginPage } from '@/pages/login-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { RequireAuth, RequirePermission } from './guards';

/**
 * Rotas da aplicação. As páginas são carregadas sob demanda (code splitting).
 * Mapeamento do protótipo: data-nav="dashboard|orders|clients|equipment|products|serviceCatalog".
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    // Página pública (sem login): o cliente assina pelo link recebido.
    path: '/assinar/:token',
    lazy: async () => ({ Component: (await import('@/pages/public-signature-page')).PublicSignaturePage }),
  },
  {
    // Portal de acompanhamento do cliente por link seguro, sem login.
    path: '/acompanhar/:token',
    lazy: async () => ({ Component: (await import('@/pages/public-portal-page')).PublicPortalPage }),
  },
  {
    // Link temporário para visualizar e baixar o PDF da OS, sem login.
    path: '/pdf-download/:token',
    lazy: async () => ({ Component: (await import('@/pages/public-pdf-download-page')).PublicPdfDownloadPage }),
  },
  {
    path: '/ordens/:id/imprimir',
    lazy: async () => {
      const { OrderPrintPage } = await import('@/pages/order-print-page');
      return {
        element: (
          <RequireAuth>
            <OrderPrintPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        handle: { crumb: 'Dashboard' },
        lazy: async () => ({ Component: (await import('@/pages/dashboard-page')).DashboardPage }),
      },
      {
        path: 'ordens',
        handle: { crumb: 'Ordens de serviço' },
        lazy: async () => ({ Component: (await import('@/pages/orders-page')).OrdersPage }),
      },
      {
        path: 'bancada',
        handle: { crumb: 'Bancada', fullWidth: true },
        lazy: async () => ({ Component: (await import('@/pages/bench-page')).BenchPage }),
      },
      {
        path: 'ordens/nova',
        handle: { crumb: 'Nova ordem' },
        lazy: async () => ({ Component: (await import('@/pages/order-detail-page')).OrderDetailPage }),
      },
      {
        path: 'ordens/:id',
        handle: { crumb: 'Ordem de serviço' },
        lazy: async () => ({ Component: (await import('@/pages/order-detail-page')).OrderDetailPage }),
      },
      {
        path: 'clientes',
        handle: { crumb: 'Clientes' },
        lazy: async () => ({ Component: (await import('@/pages/clients-page')).ClientsPage }),
      },
      {
        path: 'clientes/:id',
        handle: { crumb: 'Cliente' },
        lazy: async () => ({ Component: (await import('@/pages/client-detail-page')).ClientDetailPage }),
      },
      {
        path: 'equipamentos',
        handle: { crumb: 'Equipamentos' },
        lazy: async () => ({ Component: (await import('@/pages/equipment-page')).EquipmentPage }),
      },
      {
        path: 'produtos',
        handle: { crumb: 'Produtos' },
        lazy: async () => ({ Component: (await import('@/pages/catalog-pages')).ProductsPage }),
      },
      {
        path: 'componentes',
        handle: { crumb: 'Componentes' },
        lazy: async () => ({ Component: (await import('@/pages/components-page')).ComponentsPage }),
      },
      {
        path: 'servicos',
        handle: { crumb: 'Serviços' },
        lazy: async () => ({ Component: (await import('@/pages/catalog-pages')).ServicesPage }),
      },
      {
        path: 'estoque',
        handle: { crumb: 'Estoque' },
        lazy: async () => {
          const { StockDashboardPage } = await import('@/pages/stock-dashboard-page');
          return {
            element: (
              <RequirePermission permission="stock.adjust">
                <StockDashboardPage />
              </RequirePermission>
            ),
          };
        },
      },
      {
        path: 'estoque/receber',
        handle: { crumb: 'Entrada em lote' },
        lazy: async () => {
          const { StockReceivePage } = await import('@/pages/stock-receive-page');
          return {
            element: (
              <RequirePermission permission="stock.adjust">
                <StockReceivePage />
              </RequirePermission>
            ),
          };
        },
      },
      {
        path: 'usuarios',
        handle: { crumb: 'Usuários' },
        lazy: async () => {
          const { UsersPage } = await import('@/pages/users-page');
          return {
            element: (
              <RequirePermission permission="users.manage">
                <UsersPage />
              </RequirePermission>
            ),
          };
        },
      },
      {
        path: 'categorias',
        handle: { crumb: 'Categorias de equipamentos' },
        lazy: async () => {
          const { EquipmentCategoriesPage } = await import('@/pages/equipment-categories-page');
          return {
            element: (
              <RequirePermission permission="categories.manage">
                <EquipmentCategoriesPage />
              </RequirePermission>
            ),
          };
        },
      },
      {
        path: 'financeiro',
        handle: { crumb: 'Financeiro' },
        lazy: async () => {
          const { FinancePage } = await import('@/pages/finance-page');
          return {
            element: (
              <RequirePermission permission="finance.view">
                <FinancePage />
              </RequirePermission>
            ),
          };
        },
      },
      {
        path: 'configuracoes',
        handle: { crumb: 'Configurações' },
        lazy: async () => ({ Component: (await import('@/pages/settings-page')).SettingsPage }),
      },
      { path: 'dashboard', element: <Navigate to="/" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
